import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  Snackbar,
  Alert,
  Fade,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { API_URL } from "../../../Config/api"; // Adjust path if necessary

// --- Premium Modal Styling ---
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 450 },
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)",
  outline: "none",
  overflow: "hidden",
};

const ConsentFormModal = ({ open, onClose, eventID, isConsentFilled }) => {
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Checking if already filled based on table row data
  const alreadyFilled = String(isConsentFilled) === "true";

const handleConfirmConsentYes = async () => {
  setLoading(true);
  try {

    const resSms = await axios.post(`${API_URL}/concent_sms/${eventID}`);


    const resMail = await axios.post(`${API_URL}/concent_mail/${eventID}`);

    
    const resWhatsapp = await axios.post(
      `${API_URL}/consent_through_whatsapp/${eventID}`,
    );

   
    if (
      resSms.data?.message === "form already submitted" ||
      resMail.data?.message === "form already submitted" ||
      resWhatsapp.data?.message === "form already submitted"
    ) {
      setSnackbarMessage("Form already submitted!");
      setSnackbarSeverity("warning");
    } else {
      setSnackbarMessage("Consent forms sent successfully!");
      setSnackbarSeverity("success");
    }
  } catch (error) {
    console.error("API Error:", error);
    setSnackbarMessage("Error sending consent forms.");
    setSnackbarSeverity("error");
  } finally {
    setLoading(false);
    setSnackbarOpen(true);
    // Automatically close modal after a short delay
    setTimeout(() => {
      onClose();
    }, 1500);
  }
};

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Fade in={open}>
          <Box sx={modalStyle}>
            {/* --- Premium Header --- */}
            <Box
              sx={{
                background: "rgb(173, 219, 177)",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#ffffff",
                  letterSpacing: "0.5px",
                  textShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                Consent Form
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* --- Modal Body --- */}
            <Box sx={{ p: 4, textAlign: "center" }}>
              {alreadyFilled ? (
                <>
                  <Typography
                    sx={{
                      mb: 3,
                      color: "#166534",
                      fontWeight: 600,
                      fontSize: "16px",
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      py: 1.5,
                      borderRadius: "10px",
                    }}
                  >
                    Consent form already submitted.
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                    sx={{
                      borderRadius: "10px",
                      py: 1,
                      textTransform: "none",
                      color: "#555",
                      borderColor: "#ccc",
                    }}
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      mb: 4,
                      fontSize: "16px",
                      color: "#444",
                      fontWeight: 500,
                    }}
                  >
                    Do you want to send the consent form to the patient?
                  </Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      onClick={handleConfirmConsentYes}
                      disabled={loading}
                      sx={{
                        flex: 1,
                        background: "rgb(173, 219, 177)",
                        color: "#2c3e50",
                        fontWeight: 600,
                        py: 1.2,
                        borderRadius: "10px",
                        boxShadow: "0px 4px 12px rgba(173, 219, 177, 0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background: "rgb(143, 199, 147)",
                        },
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Yes, Send"
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onClose}
                      disabled={loading}
                      sx={{
                        flex: 1,
                        borderRadius: "10px",
                        py: 1.2,
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                    >
                      No, Cancel
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            fontWeight: 500,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ConsentFormModal;
